import { useState, useCallback, useEffect } from 'react';
import { announceToScreenReader } from '@/utils/accessibility';

export interface ValidationRule<T> {
  field: keyof T;
  message: string;
  validate: (value: any, formData: T) => boolean;
  realTime?: boolean; // Enable real-time validation for this field
}

export interface FieldState {
  touched: boolean;
  dirty: boolean;
  error?: string;
}

export interface ValidationErrors {
  [key: string]: string;
}

export function useFormValidation<T extends Record<string, any>>(
  initialData: T,
  rules: ValidationRule<T>[],
  options?: { announceErrors?: boolean }
) {
  const [formData, setFormData] = useState<T>(initialData);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>({});
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = useCallback((field: keyof T, value: any) => {
    const fieldRules = rules.filter(rule => rule.field === field);
    const fieldErrors: string[] = [];

    fieldRules.forEach(rule => {
      if (!rule.validate(value, formData)) {
        fieldErrors.push(rule.message);
      }
    });

    return fieldErrors[0] || '';
  }, [rules, formData]);

  const validateAll = useCallback(() => {
    const newErrors: ValidationErrors = {};
    let hasErrors = false;

    Object.keys(formData).forEach(field => {
      const error = validateField(field as keyof T, formData[field as keyof T]);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    setIsValid(!hasErrors);
    return !hasErrors;
  }, [formData, validateField]);

  const updateField = useCallback((field: keyof T, value: any) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    const fieldKey = String(field);
    
    // Update field state
    setFieldStates(prev => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey],
        dirty: true,
        touched: true
      }
    }));

    // Real-time validation for enabled fields
    const fieldRules = rules.filter(rule => rule.field === field);
    const shouldValidateRealTime = fieldRules.some(rule => rule.realTime);
    
    if (shouldValidateRealTime || fieldStates[fieldKey]?.touched) {
      const error = validateField(field, value);
      setErrors(prev => {
        const newErrors = { ...prev, [fieldKey]: error };
        
        // Announce error to screen reader if enabled
        if (error && options?.announceErrors) {
          announceToScreenReader(`Error in ${fieldKey}: ${error}`, 'assertive');
        }
        
        return newErrors;
      });
    }
  }, [formData, validateField, rules, fieldStates, options?.announceErrors]);

  const resetForm = useCallback(() => {
    setFormData(initialData);
    setErrors({});
    setIsValid(false);
  }, [initialData]);

  const markFieldTouched = useCallback((field: keyof T) => {
    const fieldKey = String(field);
    setFieldStates(prev => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey],
        touched: true
      }
    }));
  }, []);

  const getFieldProps = useCallback((field: keyof T) => {
    const fieldKey = String(field);
    return {
      error: errors[fieldKey],
      touched: fieldStates[fieldKey]?.touched || false,
      dirty: fieldStates[fieldKey]?.dirty || false,
      'aria-invalid': !!errors[fieldKey],
      'aria-describedby': errors[fieldKey] ? `${fieldKey}-error` : undefined
    };
  }, [errors, fieldStates]);

  const setSubmitting = useCallback((submitting: boolean) => {
    setIsSubmitting(submitting);
  }, []);

  return {
    formData,
    errors,
    fieldStates,
    isValid,
    isSubmitting,
    updateField,
    validateAll,
    resetForm,
    setFormData,
    markFieldTouched,
    getFieldProps,
    setSubmitting
  };
}